import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { LifeBuoy, X, Send } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function HelpButton({ onToast }) {
    const [open, setOpen] = useState(false);
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [subject, setSubject] = useState("");
    const [message, setMessage] = useState("");
    const [sending, setSending] = useState(false);
    const [err, setErr] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        if (!name.trim() || name.trim().length < 2) {
            setErr("Please enter your name.");
            return;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            setErr("Please enter a valid email.");
            return;
        }
        if (message.trim().length < 5) {
            setErr("Add a short message so we can help.");
            return;
        }
        setSending(true);
        try {
            await axios.post(`${API}/contact`, {
                name: name.trim(),
                email: email.trim(),
                subject: subject.trim(),
                message: message.trim(),
            });
            onToast && onToast("Message sent 🎉 We'll reply within a day.");
            setOpen(false);
            setName("");
            setEmail("");
            setSubject("");
            setMessage("");
        } catch (e) {
            setErr("Couldn't send just now — please try again.");
        } finally {
            setSending(false);
        }
    };

    return (
        <>
            <div className="fixed right-4 md:right-6 z-[390]" style={{ bottom: "152px" }}>
                <button
                    onClick={() => setOpen(true)}
                    className="w-12 h-12 rounded-full bg-white shadow-xl border border-slate-200 flex items-center justify-center hover:shadow-2xl hover:-translate-y-0.5 transition-all"
                    data-testid="help-open-btn"
                    aria-label="help & contact"
                    title="Help / Contact us"
                >
                    <LifeBuoy className="w-5 h-5 text-[#e85812]" />
                </button>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[600] bg-[#1a1e2c]/60 backdrop-blur-sm flex md:items-center items-end justify-center p-0 md:p-4"
                        onClick={() => setOpen(false)}
                        data-testid="help-modal"
                    >
                        <motion.form
                            initial={{ y: 32, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            exit={{ y: 32, opacity: 0 }}
                            transition={{ type: "spring", damping: 25, stiffness: 220 }}
                            onSubmit={submit}
                            onClick={(e) => e.stopPropagation()}
                            className="w-full md:max-w-md bg-white rounded-t-3xl md:rounded-3xl shadow-2xl overflow-hidden"
                        >
                            <div className="p-5 pb-3 flex items-start justify-between" style={{ background: "linear-gradient(180deg,#ffdccb,#ffffff)" }}>
                                <div>
                                    <div className="font-display font-extrabold text-[19px] text-[#1a1e2c]">Get in touch</div>
                                    <div className="text-[12.5px] text-slate-600 mt-0.5">Questions, feedback, or issue? We&apos;ll reply within a business day.</div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setOpen(false)}
                                    className="w-9 h-9 rounded-full bg-white shadow flex items-center justify-center"
                                    data-testid="help-close-btn"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                            <div className="p-5 pt-2 space-y-3">
                                <div className="m3-field">
                                    <input
                                        id="hf-name"
                                        type="text"
                                        placeholder=" "
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        data-testid="help-name-input"
                                        autoComplete="name"
                                    />
                                    <label htmlFor="hf-name">Your name</label>
                                </div>
                                <div className="m3-field">
                                    <input
                                        id="hf-email"
                                        type="email"
                                        placeholder=" "
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        data-testid="help-email-input"
                                        autoComplete="email"
                                    />
                                    <label htmlFor="hf-email">Email</label>
                                </div>
                                <div className="m3-field">
                                    <input
                                        id="hf-subject"
                                        type="text"
                                        placeholder=" "
                                        value={subject}
                                        onChange={(e) => setSubject(e.target.value)}
                                        data-testid="help-subject-input"
                                    />
                                    <label htmlFor="hf-subject">Subject (optional)</label>
                                </div>
                                <div className="m3-field">
                                    <textarea
                                        id="hf-msg"
                                        placeholder=" "
                                        rows={4}
                                        value={message}
                                        onChange={(e) => setMessage(e.target.value)}
                                        data-testid="help-message-input"
                                        className="!min-h-[110px]"
                                    />
                                    <label htmlFor="hf-msg">How can we help?</label>
                                </div>
                                {err && <div className="text-[13px] text-red-600" data-testid="help-error">{err}</div>}
                                <button
                                    type="submit"
                                    disabled={sending}
                                    className="btn-primary w-full !py-3 !text-[15px]"
                                    data-testid="help-submit-btn"
                                >
                                    <Send className="w-4 h-4" />
                                    {sending ? "Sending…" : "Send message"}
                                </button>
                                <div className="text-[11.5px] text-slate-500 text-center">
                                    Or email us directly at <a href="mailto:sajintexas@gmail.com" className="text-[#e85812] underline">sajintexas@gmail.com</a>
                                </div>
                            </div>
                        </motion.form>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
